import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { ApiError, api, type SnapshotDto } from '../lib/api';
import { Funnel } from '../components/Funnel';
import { StaleList, staleTitle } from '../components/StaleList';
import { ClosedList } from '../components/ClosedList';
import { WonLostFigures } from '../components/WonLostFigures';
import { Trend, type TrendSeries } from '../components/Trend';
import { weekStart } from '../../shared/kpi';

/** Letzte acht Wochen: je Woche der jüngste Snapshot. */
function weeklyPoints(snaps: SnapshotDto[], pick: (s: SnapshotDto) => number) {
  const byWeek = new Map<string, SnapshotDto>();
  for (const s of snaps) byWeek.set(weekStart(s.snapshotDate), s);
  return [...byWeek.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .slice(-8)
    .map(([week, s]) => ({ date: week, value: pick(s) }));
}

export function DetailPage() {
  const { id = '' } = useParams();
  const [own, setOwn] = useState<SnapshotDto[] | null>(null);
  const [reference, setReference] = useState<{ name: string; snaps: SnapshotDto[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [mine, team] = await Promise.all([api.history(id), api.team()]);
        if (cancelled) return;
        setOwn(mine.snapshots);
        const ref = team.team.find((m) => m.isReference && m.zohoUserId !== id);
        if (ref) {
          const refHistory = await api.history(ref.zohoUserId);
          if (!cancelled) setReference({ name: ref.name, snaps: refHistory.snapshots });
        }
      } catch (e) {
        if (!cancelled) {
          setOwn([]);
          setError(e instanceof ApiError ? e.message : 'Daten konnten nicht geladen werden.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const latest = own?.at(-1) ?? null;

  const series = (pick: (s: SnapshotDto) => number): TrendSeries[] => {
    const out: TrendSeries[] = [{ label: latest?.maName ?? 'eigene', points: weeklyPoints(own ?? [], pick) }];
    if (reference) out.push({ label: `${reference.name} (Referenz)`, points: weeklyPoints(reference.snaps, pick), dashed: true });
    return out;
  };

  return (
    <div className="container stack">
      <Link to="/" className="back-btn">
        <ChevronLeft size={16} /> Übersicht
      </Link>

      {error && <div className="alert alert--error">{error}</div>}
      {own === null && <p className="card__meta">Wird geladen…</p>}
      {own?.length === 0 && !error && <div className="empty">Für diese Person gibt es noch keinen Snapshot.</div>}

      {latest && (
        <>
          <div>
            <div className="eyebrow">// person</div>
            <h1>{latest.maName}</h1>
            <p className="card__meta">Stand {latest.snapshotDate}</p>
          </div>

          <section className="card">
            <h2>Offene Deals nach Stufe</h2>
            <Funnel buckets={latest.payload.funnelDeals} />
          </section>

          <section className="card">
            <h2>Offene Leads nach Status</h2>
            <Funnel buckets={latest.payload.funnelLeads} variant="leads" showAmount={false} />
            {latest.payload.leadsUntouched > 0 && (
              <p className="card__meta" style={{ marginTop: '0.5rem' }}>
                Davon <span className="mono">{latest.payload.leadsUntouched}</span> seit dem Anlegen
                unberührt – typisch für einen Import, nicht für bearbeitete Leads.
              </p>
            )}
          </section>

          <section className="card">
            <h2>{staleTitle(latest.payload.staleBasis)}</h2>
            <StaleList deals={latest.payload.stale} basis={latest.payload.staleBasis} />
          </section>

          <section className="card">
            <h2>Abschlüsse</h2>
            <div className="sm-card__kpis">
              <WonLostFigures label="gewonnen, Woche" value={latest.payload.wonWeek} />
              <WonLostFigures label="gewonnen, Monat" value={latest.payload.wonMonth} />
              <WonLostFigures label="verloren, Woche" value={latest.payload.lostWeek} />
              <WonLostFigures label="verloren, Monat" value={latest.payload.lostMonth} />
            </div>
            <p className="card__meta" style={{ marginTop: '0.5rem' }}>
              Zugeordnet nach Abschlussdatum (<span className="mono">Closing_Date</span>), nicht nach
              der letzten Änderung am Datensatz – eine Korrektur im Nachhinein verschiebt einen Deal
              sonst in die laufende Woche. Beträge stammen aus <span className="mono">Amount</span>,
              das im CRM nur teilweise gefüllt ist; fehlende werden ausgewiesen statt als 0 € gezählt.
            </p>
          </section>

          <section className="card">
            <h2>Gewonnen im Monat</h2>
            <ClosedList deals={latest.payload.wonList} kind="won" />
          </section>

          <section className="card">
            <h2>Verloren im Monat</h2>
            <ClosedList deals={latest.payload.lostList} kind="lost" />
          </section>

          <section className="card">
            <h2>Notizen</h2>
            <div className="sm-card__kpis">
              <div>
                <div className="sm-kpi__label">diese Woche</div>
                <div className="sm-kpi__value mono">{latest.payload.notesWeek}</div>
              </div>
              <div>
                <div className="sm-kpi__label">pro offenem Deal, Woche</div>
                <div className="sm-kpi__value mono">{latest.payload.notesPerActiveDeal}</div>
              </div>
            </div>
          </section>

          <section className="card">
            <h2>Verlauf, 8 Wochen</h2>
            <Trend series={series((s) => s.payload.openDealCount)} unitLabel="offene Deals" />
            <div style={{ marginTop: '1rem' }}>
              <Trend series={series((s) => s.payload.wonMonth.count)} unitLabel="gewonnen im Monat" />
            </div>
          </section>
        </>
      )}
    </div>
  );
}
